import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { PrimaryButton } from '../components/Button'
import Spinner from '../components/Spinner'
import Radom, { User } from '../state/Radom'
import { Container } from '../components/Animations'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import { errorToast, successToast } from '../util/Util'
import Modal from '../components/Modal'
import OneTimePassForm from '../components/OneTimePassForm'
import {
  MutedValue,
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const Account = observer(() => {
  const [user, setUser] = useState<User>()
  const [isLoading, setIsLoading] = useState(true)
  const [is2faModalVisible, setIs2faModalVisible] = useState(false)

  const getUser = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const u = await Radom.getUser()
      setUser(u)
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getUser()
  }, [])

  if (isLoading) {
    return <Container>
      <Spinner />
    </Container>
  }

  return <Container>
    <SettingsPage>
      <SettingsHero>
        <div>
          <SettingsEyebrow>User settings</SettingsEyebrow>
          <SettingsTitle>Security</SettingsTitle>
          <SettingsSubtext>
            Manage your sign-in identity and protect access to this dashboard.
          </SettingsSubtext>
        </div>
        <StatusPill $tone={user?.otpEnabled && user?.otpVerified ? 'green' : 'amber'}>
          {user?.otpEnabled && user?.otpVerified ? '2FA enabled' : '2FA recommended'}
        </StatusPill>
      </SettingsHero>

      <SettingsCard>
        <SettingsCardHeader>
          <div>
            <SettingsCardTitle>Account access</SettingsCardTitle>
            <SettingsCardDescription>
              These settings apply to your user account, not every member in the organization.
            </SettingsCardDescription>
          </div>
        </SettingsCardHeader>
        <SettingsCardBody>
          <SettingsRows>
            <SettingsRow>
              <div>
                <SettingsRowTitle>Sign-in identity</SettingsRowTitle>
                <SettingsRowDescription>
                  The email address or wallet attached to this dashboard login.
                </SettingsRowDescription>
              </div>
              <MutedValue>{user?.emailAddress || user?.evmAddress}</MutedValue>
            </SettingsRow>
            <SettingsRow>
              <div>
                <SettingsRowTitle>Two-factor authentication</SettingsRowTitle>
                <SettingsRowDescription>
                  Require a one-time passcode when signing in to reduce account takeover risk.
                </SettingsRowDescription>
              </div>
              {user?.otpEnabled && user?.otpVerified
                ? <ConfirmButtonModal
                  title='Disable'
                  description='Are you sure you want to disable two-factor authentication?'
                  action='Disable'
                  onConfirm={async () => {
                    try {
                      await Radom.disableOneTimePassword()
                      successToast('Two-factor authentication disabled')
                      getUser()
                    } catch (err) {
                      errorToast(err.response?.data?.error || err.message)
                    }
                  }}
                  ActionComponent={props =>
                    <PrimaryButton {...props}>
                      <span>Disable</span>
                    </PrimaryButton>
                  }
                />
                : <PrimaryButton onClick={() => setIs2faModalVisible(true)}>
                  Enable 2FA
                </PrimaryButton>}
            </SettingsRow>
          </SettingsRows>
        </SettingsCardBody>
      </SettingsCard>

      <Modal
        title='Two-factor authentication'
        visible={is2faModalVisible}
        onClose={() => setIs2faModalVisible(false)}
        innerStyles={{ width: 'min(520px, calc(100vw - 40px))' }}>
        <div style={{ maxWidth: 520 }}>
          <OneTimePassForm onTwoFactorSetSuccess={ () => {
            successToast('Successfully set two-factor authentication')
            setIs2faModalVisible(false)
            getUser()
          }} />
        </div>
      </Modal>

    </SettingsPage>
  </Container>
})

export default Account
